/** @format */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { getCentrifugoClient } from "@/api/centrifugoClient";
import { toCamelCase } from "@/api/galaxieClient";
import type { Notification } from "@/api/notificationsApi";
import { useUser } from "@/api/userAPI";
import { useStableFeatureFlags } from "@/hooks/useStableFeatureFlags";
import { useConfigStore } from "@/stores/configStore";
import { useNotificationToastStore } from "@/stores/notificationToastStore";

interface NotificationPayload {
  type: string;
  data?: {
    type: string;
    category: string;
    title: string;
    body: string;
    data: Record<string, unknown>;
    timestamp: string;
    event_id: string | null;
  };
}

/**
 * Listen for user notifications via Centrifugo personal channel.
 *
 * Centrifugo auto-subscribes the user to their personal channel (``#userId``)
 * via ``user_subscribe_to_personal`` in the server config. This hook listens
 * for publications on that server-side subscription rather than creating a
 * client-side subscription (which would fail with "already subscribed").
 */
export const useCentrifugoNotifications = () => {
  const { data: user } = useUser();
  const queryClient = useQueryClient();
  const flags = useStableFeatureFlags();
  const notificationsEnabled = flags?.includes("notifications") ?? false;
  const websocketsEnabled = useConfigStore(
    state => state.config?.websocketsEnabled ?? false
  );

  useEffect(() => {
    if (!notificationsEnabled || !user?.id || !websocketsEnabled) {
      return;
    }

    const client = getCentrifugoClient();

    // Listen for publications on server-side subscriptions (personal channel).
    // Centrifugo delivers these via the client-level "publication" event since
    // the subscription is managed server-side, not client-side.
    const handlePublication = (ctx: { channel: string; data: unknown }) => {
      const personalChannel = `#${user.id}`;
      if (ctx.channel !== personalChannel) {
        return;
      }

      const raw = ctx.data as NotificationPayload;
      const data = toCamelCase(raw) as NotificationPayload;

      if (data.type === "notification" && data.data) {
        const notification = toCamelCase(data.data) as unknown as Notification;

        queryClient.setQueryData<Notification[]>(
          ["notifications", "history"],
          (old = []) => [notification, ...old].slice(0, 50)
        );

        queryClient.setQueryData<number>(
          ["notifications", "unread-count"],
          (old = 0) => old + 1
        );

        useNotificationToastStore.getState().addToast(notification);
      }
    };

    client.on("publication", handlePublication);

    return () => {
      client.removeListener("publication", handlePublication);
    };
  }, [user?.id, notificationsEnabled, websocketsEnabled, queryClient]);
};
